import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Instagram, CheckCircle2, Clock, Shield, HeadphonesIcon } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { isOfferInStock } from '../data/offers';

export const OfferDetailsModal = ({ offer, isOpen, onClose }) => {
  if (!offer) return null;

  const inStock = isOfferInStock(offer);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-card max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="text-5xl">{offer.logo}</div>
            <div>
              <DialogTitle className="text-2xl font-bold text-foreground">
                {offer.serviceName} {offer.planName}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                {inStock
                  ? `Premium subscription at ${offer.discountPercent}% discount`
                  : 'Currently out of stock - restocking soon'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Pricing Section */}
            <div className="glass-card p-6 rounded-lg space-y-4">
              {inStock ? (
                <>
                  <div className="flex items-baseline gap-3">
                    <div className="inline-flex items-center gap-2 bg-success/20 text-success px-4 py-2 rounded-full">
                      <span className="text-3xl font-bold">{offer.discountPercent}%</span>
                      <span className="text-xs font-medium uppercase">OFF</span>
                    </div>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-4xl font-bold text-foreground">
                      {offer.currencySymbol}{offer.finalPrice.toFixed(2)}
                    </span>
                    <span className="text-2xl text-muted-foreground line-through">
                      {offer.currencySymbol}{offer.originalPrice.toFixed(2)}
                    </span>
                    <span className="text-lg text-muted-foreground">{offer.billingPeriod}</span>
                  </div>
                  {offer.badge && (
                    <Badge className="shimmer text-sm font-semibold">{offer.badge}</Badge>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <div className="inline-flex items-center gap-2 bg-warning/15 text-warning px-4 py-2 rounded-full">
                      <Clock className="w-5 h-5" />
                      <span className="font-semibold">Out of stock</span>
                    </div>
                    <Badge variant="outline" className="border-warning/40 bg-warning/10 text-warning">
                      Coming soon
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Restocking soon. Prices and discounts will appear once this offer is available again.
                  </p>
                </>
              )}
            </div>

            {/* What You Get */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary" />
                What You Get
              </h3>
              <p className="text-muted-foreground leading-relaxed">{offer.details}</p>
              <div className="space-y-2">
                {offer.highlights.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Activation Steps */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Shield className="w-5 h-5 text-accent" />
                How Activation Works
              </h3>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Contact us on Instagram</p>
                    <p className="text-sm text-muted-foreground">Send us a message with your chosen offer</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <div>
                    <p className="font-medium text-foreground">We activate with you</p>
                    <p className="text-sm text-muted-foreground">We guide you through the process in real-time</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Confirm it works</p>
                    <p className="text-sm text-muted-foreground">Verify the subscription is active and working</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-success/20 text-success flex items-center justify-center font-bold text-sm">
                    4
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Pay after activation</p>
                    <p className="text-sm text-muted-foreground">Only pay once everything is confirmed working</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Delivery & Support */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="glass-card p-4 rounded-lg space-y-2">
                <Clock className="w-5 h-5 text-primary" />
                <p className="font-medium text-foreground text-sm">Delivery Time</p>
                <p className="text-xs text-muted-foreground">
                  {inStock ? offer.deliveryNote : 'Restocking soon'}
                </p>
              </div>
              <div className="glass-card p-4 rounded-lg space-y-2">
                <HeadphonesIcon className="w-5 h-5 text-accent" />
                <p className="font-medium text-foreground text-sm">Support Promise</p>
                <p className="text-xs text-muted-foreground">{offer.supportNote}</p>
              </div>
            </div>

            {/* Terms & Eligibility */}
            <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium text-foreground">Terms & Eligibility</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Prices and availability can vary by region and eligibility requirements. Confirm details in Instagram DM
                before purchase. We are not affiliated with or endorsed by {offer.serviceName}.
              </p>
            </div>
          </div>
        </ScrollArea>

        {/* Action Button */}
        <div className="pt-4 border-t border-border/50">
          <Button
            variant={inStock ? 'default' : 'outline'}
            className={`w-full shadow-elegant font-semibold text-base py-6 ${
              inStock
                ? 'bg-primary hover:bg-primary-glow text-primary-foreground'
                : 'border-warning/30 bg-warning/10 text-warning hover:bg-warning/15 hover:text-warning'
            }`}
            onClick={() => window.open(offer.ctaInstagramUrl, '_blank')}
          >
            <Instagram className="w-5 h-5 mr-2" />
            {inStock ? 'Buy via Instagram' : 'Notify on Instagram'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
